import { Avatar } from "@mantine/core"
import { resolveSpeakerColor, speakerInkColor } from "../../lib/speaker-colors"
import { speakerInitials } from "../../lib/speaker-format"
import type { IProps } from "./interfaces"

import classes from "./styles.module.css"

/**
 * AvatarStack — overlapping circle of speaker initials. Pure presentational.
 * Shows up to `max` avatars (each tinted by the canonical speaker colour chain)
 * and collapses any remainder into a neutral "+N" chip.
 *
 * Colours resolve through `resolveSpeakerColor` (the single chain, audit 3.13):
 * the per-meeting `colorMap` when supplied — so a saved override wins — else the
 * shared deterministic hash. Initials come from the one canonical
 * `speakerInitials` formatter.
 */
export const AvatarStack = ({ names, max = 4, colorMap }: IProps): React.JSX.Element => {
  const visible = names.slice(0, max)
  const overflow = names.length - visible.length
  return (
    <div className={classes.avStack}>
      {visible.map((name) => {
        const color = resolveSpeakerColor(name, colorMap)
        return (
          <Avatar
            key={name}
            size={22}
            radius="xl"
            color={color}
            variant="filled"
            name={name}
            title={name}
            // Avatar autoContrast doesn't fire for non-theme (identity-hex) colours,
            // so white initials failed on the light hues — pin per-hue ink.
            styles={{ placeholder: { color: speakerInkColor(name, colorMap) } }}
          >
            {speakerInitials(name)}
          </Avatar>
        )
      })}
      {overflow > 0 && (
        // Light variant (gray tint + the WCAG-safe gray label) instead of a
        // filled mid-gray, where white initials fail in dark mode.
        <Avatar size={22} radius="xl" color="gray" variant="light">
          +{overflow}
        </Avatar>
      )}
    </div>
  )
}
